import {
  InvoiceReadable,
  invoicesDownloadRetrieveOptions,
  invoicesListOptions,
  invoicesResendEmailRetrieveOptions
} from '@crm/api'
import { AppLink } from '@crm/components'
import { EditInvoiceModal } from '@crm/features/billing/components'
import { getRoute, TagBillingStatuses } from '@crm/utils'
import {
  ConditionalText,
  DayLabel,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import { useQueryUpdate, useTableSorter } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters, downloadFile, showApiErrors } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Button, notification, Tag } from 'antd'

import { MenuProps } from 'antd/lib'
import { Back, DocumentDownload, Edit2 } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { TableColumnsType } from 'antd'

export const InvoicesPage = () => {
  const { params } = useQueryUpdate()
  const { t } = useLocales()
  const [editInvoice, setEditInvoice] = useState<InvoiceReadable>()
  const [invoice, setInvoice] = useState<InvoiceReadable>()
  const sorter = useTableSorter()

  const { data, isLoading } = useQuery(
    invoicesListOptions({
      query: { ...defaultFilters, ...params }
    })
  )

  const { refetch: resendEmail } = useQuery({
    ...invoicesResendEmailRetrieveOptions({
      path: { id: Number(invoice?.id) }
    }),
    enabled: false
  })

  const { refetch: download } = useQuery({
    ...invoicesDownloadRetrieveOptions({
      path: { id: Number(invoice?.id) }
    }),
    enabled: false
  })

  const onResendEmail = useCallback(
    (invoice: InvoiceReadable) => {
      setInvoice(invoice)
      resendEmail()
    },
    [resendEmail]
  )

  const onDownload = useCallback(
    (invoice: InvoiceReadable) => {
      setInvoice(invoice)

      try {
        requestAnimationFrame(async () => {
          const blob = await download()
          if (blob?.data) {
            downloadFile(blob.data, `${invoice.id}.pdf`)
            notification.success({
              message: t('success.success'),
              description: t('success.export_started')
            })
          }
        })
      } catch (error) {
        showApiErrors(error)
      }
    },
    [download, t]
  )

  const getActionColumnItem: (invoice: InvoiceReadable) => MenuProps['items'] =
    useMemo(
      () => (invoice) =>
        [
          {
            label: t('campaigns.edit'),
            key: 'edit',
            icon: <Edit2 size={20} />,
            onClick: () => setEditInvoice(invoice)
          },
          {
            label: t('billing.resend_email'),
            key: 'resend_email',
            icon: <Back size={20} />,
            onClick: () => onResendEmail(invoice)
          },
          { type: 'divider' },
          {
            label: t('billing.download'),
            key: 'delete',
            icon: <DocumentDownload size={20} />,
            onClick: () => onDownload(invoice)
          }
        ],
      [onDownload, onResendEmail, t]
    )

  const columns = useMemo<TableColumnsType<InvoiceReadable>>(
    () => [
      {
        title: t('billing.invoice'),
        dataIndex: 'invoice_number',
        key: 'invoice_number',
        sorter: true,
        render: (invoiceNumber, { id }) =>
          invoiceNumber && id ? (
            <AppLink to={getRoute('BillingInvoicesDetails', { id })}>
              {invoiceNumber}
            </AppLink>
          ) : (
            '-'
          )
      },
      {
        title: t('billing.contractor'),
        dataIndex: 'lead',
        key: 'lead__name',
        sorter: true,
        render: (lead) =>
          lead?.id && lead?.name ? (
            <AppLink to={`/leads/${lead?.id}`}>{lead?.name}</AppLink>
          ) : (
            '-'
          )
      },
      {
        title: t('billing.currency'),
        dataIndex: ['currency', 'name'],
        render: (currencyName) => (
          <ConditionalText condition={!!currencyName}>
            {currencyName}
          </ConditionalText>
        )
      },
      {
        title: t('billing.title'),
        dataIndex: 'title',
        key: 'title',
        sorter: true,
        render: (title, { id }) =>
          title && id ? (
            <AppLink to={getRoute('BillingInvoicesDetails', { id })}>
              {title}
            </AppLink>
          ) : (
            '-'
          )
      },
      {
        title: t('billing.product'),
        dataIndex: ['product', 'name'],
        key: 'product__name',
        sorter: true,
        render: (productName) => (
          <ConditionalText condition={!!productName}>
            {productName}
          </ConditionalText>
        )
      },
      {
        title: t('billing.created'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (createdAt) => <DayLabel date={createdAt} />
      },
      {
        title: t('billing.due_date'),
        dataIndex: 'due_date',
        key: 'due_date',
        sorter: true,
        render: (dueDate) => <DayLabel date={dueDate} />
      },
      {
        title: t('billing.pay_date'),
        dataIndex: 'pay_date',
        render: (payDate) => <DayLabel date={payDate} />
      },
      {
        title: t('billing.status'),
        key: 'status',
        sorter: true,
        render: (invoice) => (
          <Tag
            color={
              TagBillingStatuses[
                invoice.status as keyof typeof TagBillingStatuses
              ]
            }
          >
            {t(`billing.${invoice.status}`)}
          </Tag>
        )
      },

      {
        width: 50,
        fixed: 'right',
        render: (invoice) => (
          <TableActions options={getActionColumnItem(invoice)} />
        ),

        onCell: () => ({
          onClick: (e) => e.stopPropagation()
        })
      }
    ],
    [getActionColumnItem, t]
  )

  const onEditInvoiceModalClose = () => setEditInvoice(undefined)

  return (
    <>
      <TitleSection
        title={t('menu.invoices')}
        count={data?.count}
        isLoading={isLoading}
      />

      <TableContainer
        loading={isLoading}
        data={data}
        columns={columns}
        isHoverAlternative
        onChange={(_, __, result) => sorter(result)}
        rightChildren={
          <AppLink to={getRoute('BillingInvoicesCreate')}>
            <Button type="primary">{t('billing.create_invoice')}</Button>
          </AppLink>
        }
      />

      <EditInvoiceModal
        open={!!editInvoice}
        invoice={editInvoice}
        onClose={onEditInvoiceModalClose}
      />
    </>
  )
}
